import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

// Lazy-load html2canvas from CDN once; resolve to the global.
let _h2cPromise: Promise<any> | null = null;
function loadHtml2Canvas(): Promise<any> {
  if ((window as any).html2canvas) return Promise.resolve((window as any).html2canvas);
  if (_h2cPromise) return _h2cPromise;
  _h2cPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    s.onload = () => resolve((window as any).html2canvas);
    s.onerror = () => reject(new Error('html2canvas failed to load'));
    document.head.appendChild(s);
  });
  return _h2cPromise;
}

function cssPath(el: HTMLElement): string {
  const parts: string[] = [];
  let node: HTMLElement | null = el;
  let depth = 0;
  while (node && node.nodeType === 1 && depth < 5) {
    let part = node.tagName.toLowerCase();
    if (node.id) { parts.unshift(part + '#' + node.id); break; }
    const cls = (typeof node.className === 'string' && node.className.trim())
      ? '.' + node.className.trim().split(/\s+/).slice(0, 2).join('.')
      : '';
    parts.unshift(part + cls);
    node = node.parentElement;
    depth++;
  }
  return parts.join(' > ').slice(0, 240);
}

// Render an element to a size-bounded JPEG; returns base64 (no data: prefix) or null.
async function snapshotImage(el: HTMLElement): Promise<{ b64: string; mime: string } | null> {
  try {
    const h2c = await loadHtml2Canvas();
    const rectW = el.getBoundingClientRect().width || 800;
    const scale = Math.min(1, 1100 / Math.max(rectW, 1));
    const canvas = await h2c(el, { scale, backgroundColor: getComputedStyle(document.body).backgroundColor || '#fff', logging: false, useCORS: true });
    let quality = 0.72;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    // keep payload reasonable (<~500KB base64); step quality down if needed
    while (dataUrl.length > 700_000 && quality > 0.4) { quality -= 0.15; dataUrl = canvas.toDataURL('image/jpeg', quality); }
    const b64 = dataUrl.split(',')[1] ?? '';
    if (!b64 || dataUrl.length > 1_200_000) return null;
    return { b64, mime: 'image/jpeg' };
  } catch {
    return null; // best-effort; text snapshot still files
  }
}

export default function QaCapture() {
  const [on, setOn] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const capture = useCallback(async (e: MouseEvent) => {
    const el = e.target as HTMLElement | null;
    if (!el) return;
    if (el.closest('[data-qa-capture-ui]')) return;
    e.preventDefault();
    e.stopPropagation();
    const container = (el.closest('.card, section, main, [role="region"]') as HTMLElement) ?? el;
    const rect = el.getBoundingClientRect();
    const route = location.hash ? location.hash.replace(/^#/, '') : location.pathname;
    setToast('Capturing screenshot…');
    const img = await snapshotImage(container);
    try {
      await api.qaUiReport({
        route,
        url: location.href,
        page_title: document.title,
        selector: cssPath(el),
        tag: el.tagName.toLowerCase(),
        element_text: (el.innerText || el.textContent || '').trim().slice(0, 300),
        context_text: (container.innerText || '').trim().slice(0, 1200),
        rect: { x: Math.round(rect.x), y: Math.round(rect.y), w: Math.round(rect.width), h: Math.round(rect.height) },
        viewport: { w: window.innerWidth, h: window.innerHeight },
        captured_at: new Date().toISOString(),
        image_base64: img?.b64,
        image_mime: img?.mime,
      });
      setToast(img ? 'Saved to QA Sentry ✓ (screenshot + context)' : 'Saved to QA Sentry ✓ (text only — image unavailable)');
      setTimeout(() => setToast(null), 4000);
    } catch (err: any) {
      const detail = (err?.message ?? 'error') + (err?.status ? ` (HTTP ${err.status})` : '');
      setToast('Capture failed: ' + detail);
      setTimeout(() => setToast(null), 9000);  // keep the real error on screen long enough to read
    }
  }, []);

  useEffect(() => {
    if (!on) return;
    document.addEventListener('click', capture, true);
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'crosshair';
    return () => {
      document.removeEventListener('click', capture, true);
      document.body.style.cursor = prevCursor;
    };
  }, [on, capture]);

  return (
    <div data-qa-capture-ui style={{ position: 'fixed', right: 20, bottom: 88, zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {toast && (
        <div style={{ marginBottom: 8, padding: '6px 10px', borderRadius: 8, background: '#111827', color: '#fff', fontSize: 12, maxWidth: 300, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
          {toast}
        </div>
      )}
      <button
        onClick={() => { setOn((v) => !v); setToast(on ? null : 'QA Capture ON — click any error area'); }}
        title="When ON, click any error area on the page to snapshot it (image + context) into QA Sentry / RADAR"
        style={{
          padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          border: on ? '1px solid #b00020' : '1px solid #9ca3af',
          background: on ? '#b00020' : '#ffffff',
          color: on ? '#ffffff' : '#374151',
          boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
        }}
      >
        {on ? '● QA Capture ON — click an error' : '◎ QA Capture'}
      </button>
    </div>
  );
}
